import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import { QRCodeCanvas } from 'qrcode.react';
import { downloadCanvasAsPng } from '../../../utils/qrUtils';
import { createApiRequest } from '../../../utils/apiUtils';
import { PublicReservationFormView } from '../../../types/reservation';

interface Props {
  storeId: string;
}

/**
 * acceptingReservations=false のときに、店舗が何を直せば公開受付になるかを案内する。
 * BE は notAcceptingReason（SUBSCRIPTION / PAGE_DISABLED / PRIVACY_POLICY_MISSING / CONTACT_MISSING）を返す。
 * 優先順位は SUBSCRIPTION > PAGE_DISABLED > PRIVACY_POLICY_MISSING > CONTACT_MISSING。
 */
const NOT_ACCEPTING_GUIDE: Record<string, { message: string; action?: string }> = {
  PRIVACY_POLICY_MISSING: {
    message: '「個人情報の取り扱い」が未設定のため、予約ページは受付停止中です。',
    action: '「店舗情報・通知」タブの「個人情報の取り扱い（必須）」を設定すると受付を開始できます。',
  },
  CONTACT_MISSING: {
    message: '連絡先が未設定のため受付停止中です。',
    action: '「店舗情報・通知」タブの連絡先を入力してください。',
  },
  PAGE_DISABLED: {
    message: '予約ページを「非公開」に設定しているため、受付停止中です。',
    action: '「受付設定」タブの「予約ページを公開する」をオンにすると受付を開始できます。',
  },
  SUBSCRIPTION: {
    message: 'ご契約（サブスクリプション）が有効でないため、予約ページは受付停止中です。',
    action: '店舗管理のサブスクリプションをご確認ください。（すでにお預かりしている予約の対応は引き続き可能です）',
  },
};

/**
 * 予約ページの公開URLパネル（予約設定ハブ最上部・常時表示）。
 * 店舗が Google ビジネスプロフィール（Google Maps）・Instagram・自店サイト等へ掲載するための
 * URL コピー / 新規タブで開く / QR コード（店頭掲示用 PNG ダウンロード）/ プレビュー導線をまとめる。
 */
const PublicUrlPanel: React.FC<Props> = ({ storeId }) => {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  // 公開ページの受付状態（認証不要の公開フォームAPIをそのまま参照して実態を表示する）
  const [status, setStatus] = useState<Pick<
    PublicReservationFormView,
    'acceptingReservations' | 'notAcceptingReason'
  > | null>(null);

  // 公開予約ページの実URL（stg/prod はビルドの配信オリジンと一致する）
  const url = `${window.location.origin}/reservations/${storeId}`;

  const loadStatus = useCallback(() => {
    createApiRequest<PublicReservationFormView>(`/public/stores/${storeId}/reservation-form`, {
      method: 'GET',
      requiresAuth: false,
    })
      .then((form) =>
        setStatus({
          acceptingReservations: form.acceptingReservations,
          notAcceptingReason: form.notAcceptingReason,
        }),
      )
      .catch(() => setStatus(null));
  }, [storeId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const copy = () => {
    navigator.clipboard
      .writeText(url)
      .then(() => setCopied(true))
      .catch(() => {
        // クリップボードAPI不可時のフォールバック（http環境等）
        window.prompt('このURLをコピーしてください', url);
      });
  };

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2">予約ページのURL</Typography>
        {status?.acceptingReservations === true && (
          <Chip size="small" color="success" icon={<CheckCircleIcon />} label="受付中" />
        )}
        {status?.acceptingReservations === false && (
          <Chip size="small" color="warning" label="受付停止中" />
        )}
      </Box>

      {/* 公開設定にしても受付が始まらない場合の理由と対処を明示する */}
      {status?.acceptingReservations === false && (
        <Alert
          severity="warning"
          sx={{ mb: 1.5 }}
          action={
            <Button color="inherit" size="small" onClick={loadStatus}>
              再確認
            </Button>
          }
        >
          <AlertTitle>この予約ページは今お客様が予約できません</AlertTitle>
          {(() => {
            const g =
              (status.notAcceptingReason && NOT_ACCEPTING_GUIDE[status.notAcceptingReason]) || null;
            if (!g) {
              return '受付を開始するための設定が不足しています。各タブの設定をご確認ください。';
            }
            return (
              <>
                {g.message}
                {g.action && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {g.action}
                  </Typography>
                )}
              </>
            );
          })()}
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
        このURLをGoogleビジネスプロフィール（Googleマップの「予約リンク」）やSNS・自店サイトに掲載すると、お客様がネット予約できます。
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          value={url}
          size="small"
          InputProps={{ readOnly: true }}
          sx={{ flexGrow: 1, minWidth: 240 }}
          onFocus={(e) => e.target.select()}
        />
        <Tooltip title="URLをコピー">
          <IconButton onClick={copy} aria-label="URLをコピー">
            <ContentCopyIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="新しいタブで開く">
          <IconButton component="a" href={url} target="_blank" rel="noopener" aria-label="新しいタブで開く">
            <OpenInNewIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="QRコード（店頭掲示用）">
          <IconButton onClick={() => setQrOpen(true)} aria-label="QRコードを表示">
            <QrCode2Icon />
          </IconButton>
        </Tooltip>
        <Button
          size="small"
          startIcon={<VisibilityIcon />}
          onClick={() => navigate(`/${storeId}/reservation/preview`)}
        >
          プレビュー
        </Button>
      </Stack>

      <Dialog open={qrOpen} onClose={() => setQrOpen(false)}>
        <DialogTitle>予約ページのQRコード</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, p: 1 }}>
            <QRCodeCanvas ref={canvasRef} value={url} size={220} marginSize={2} level="M" />
            <Typography variant="caption" color="text.secondary">
              店頭POPやショップカードに印刷してご利用ください
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => canvasRef.current && downloadCanvasAsPng(canvasRef.current, '予約ページQR')}
          >
            PNGダウンロード
          </Button>
          <Button onClick={() => setQrOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="URLをコピーしました"
      />
    </Paper>
  );
};

export default PublicUrlPanel;
